import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Tooltip,
  Stack
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Person as PersonIcon,
  Settings,
  Logout,
  Warning as WarningIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppBranding } from '../../context/AppBrandingContext';
import Sidebar from './Sidebar';
import DigitalClock from '../common/DigitalClock';
import LogActivityHeaderButton from '../common/LogActivityHeaderButton';
import BreakCountdownOverlay from '../common/BreakCountdownOverlay';
import BreakPointerCapture from '../common/BreakPointerCapture';
import IdleAcknowledgmentOverlay from '../common/IdleAcknowledgmentOverlay';
import MarqueeBar from '../common/MarqueeBar';
import GlobalMessageBanner from '../common/GlobalMessageBanner';
import RouteErrorBoundary from '../common/RouteErrorBoundary';
import HomeIcon from '@mui/icons-material/Home';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { menuOutlinedBoxButtonSx } from '../../utils/landingThemeHelpers';
import api from '../../utils/api';
import { DEMO_PREVIEW_STORAGE_KEY, clearDemoPreviewTabSession } from '../../utils/demoPreviewStorage';
import { isMarketingEmbedMode } from '../../utils/marketingPreviewStorage';
import { buildHeaderNotifications, headerNotificationBadgeCount } from '../../utils/headerNotifications';
import { toast } from 'react-toastify';

const DRAWER_EXPANDED = 280;
const DRAWER_COLLAPSED = 72;
const LS_SIDEBAR_PINNED = 'erp4c-sidebar-pinned';
const LS_SIDEBAR_COLLAPSED = 'erp4c-sidebar-collapsed';
const UI_BUILD_STAMP = (process.env.REACT_APP_UI_BUILD_STAMP || '').trim();
const UI_VERSION_DISPLAY = UI_BUILD_STAMP || 'unknown';

function readSidebarBool(key, defaultValue) {
  try {
    const v = localStorage.getItem(key);
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
  } catch {
    /* ignore */
  }
  return defaultValue;
}

const MainLayout = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => readSidebarBool(LS_SIDEBAR_PINNED, false));
  const marketingEmbed = isMarketingEmbedMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    isMarketingEmbedMode() ? true : readSidebarBool(LS_SIDEBAR_COLLAPSED, false)
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [punchStatus, setPunchStatus] = useState({ isOnline: false, isIdle: true });
  const [punchDetail, setPunchDetail] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');
  const [versionsData, setVersionsData] = useState(null);
  const [routeErrorBoundaryKey, setRouteErrorBoundaryKey] = useState(0);
  const [demoPreviewBackup, setDemoPreviewBackup] = useState(null);
  const { user, logout } = useAuth();
  const { displayCompanyName } = useAppBranding();
  const navigate = useNavigate();
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(120);

  useEffect(() => {
    const el = appBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > 0) setAppBarHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [marketingEmbed, demoPreviewBackup?.token]);

  // Fetch punch status for marquee
  const fetchPunchStatus = useCallback(async () => {
    try {
      const response = await api.get('/punchcard/status');
      const d = response.data?.data;
      setPunchDetail(d && typeof d === 'object' ? d : null);
      setPunchStatus({
        isOnline: d?.isOnline || false,
        isIdle: d?.isIdle !== false
      });
    } catch (error) {
      console.error('Failed to fetch punch status:', error);
      setPunchDetail(null);
    }
  }, []);

  useEffect(() => {
    fetchPunchStatus();
    const interval = setInterval(fetchPunchStatus, 30 * 1000);
    return () => clearInterval(interval);
  }, [fetchPunchStatus]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DEMO_PREVIEW_STORAGE_KEY);
      setDemoPreviewBackup(raw ? JSON.parse(raw) : null);
    } catch {
      setDemoPreviewBackup(null);
    }
  }, [user?.id]);

  const exitDemoUserPreview = () => {
    const b = demoPreviewBackup;
    if (!b?.token) return;
    clearDemoPreviewTabSession();
    localStorage.setItem('token', b.token);
    if (b.refreshToken != null) localStorage.setItem('refreshToken', b.refreshToken);
    sessionStorage.removeItem(DEMO_PREVIEW_STORAGE_KEY);
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
      }
    } catch {
      /* ignore */
    }
    window.close();
    window.setTimeout(() => {
      if (!window.closed) {
        window.location.reload();
      }
    }, 200);
  };

  // Listen for punch status changes from any component
  useEffect(() => {
    const handlePunchStatusChange = () => {
      // Small delay to ensure backend has updated
      setTimeout(fetchPunchStatus, 500);
    };
    window.addEventListener('punchStatusChanged', handlePunchStatusChange);
    return () => window.removeEventListener('punchStatusChanged', handlePunchStatusChange);
  }, [fetchPunchStatus]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    try {
      localStorage.setItem(LS_SIDEBAR_PINNED, sidebarPinned ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarPinned]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_SIDEBAR_COLLAPSED, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const desktopDrawerWidth = isMdUp
    ? sidebarPinned
      ? DRAWER_EXPANDED
      : sidebarCollapsed
        ? DRAWER_COLLAPSED
        : DRAWER_EXPANDED
    : DRAWER_EXPANDED;

  const navCompact = isMdUp && !sidebarPinned && sidebarCollapsed;

  const handleToggleSidebarPin = () => {
    setSidebarPinned((p) => {
      const next = !p;
      if (next) setSidebarCollapsed(false);
      return next;
    });
  };

  const handleToggleSidebarCollapsed = () => {
    if (sidebarPinned) return;
    setSidebarCollapsed((c) => !c);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
    fetchPunchStatus();
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const openVersionsDialog = async () => {
    setVersionsOpen(true);
    setVersionsLoading(true);
    setVersionsError('');
    setVersionsData(null);
    try {
      const res = await api.get('/settings/component-versions', {
        params: { ui: UI_VERSION_DISPLAY },
      });
      setVersionsData(res.data?.data || null);
    } catch (err) {
      setVersionsError(err.response?.data?.message || err.message || 'Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  const notificationItems = buildHeaderNotifications(punchDetail);
  const notifBadgeCount = headerNotificationBadgeCount(notificationItems);

  const menuNavPath = `${location.pathname}${location.search}`;
  const menuNavFullUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${menuNavPath}` : menuNavPath;

  const notifLevelColor = (level) => {
    if (level === 'warning') return 'warning.main';
    if (level === 'error') return 'error.main';
    if (level === 'success') return 'success.main';
    return 'info.main';
  };

  const notifLevelIcon = (level) => {
    if (level === 'warning' || level === 'error') return <WarningIcon sx={{ fontSize: 20 }} />;
    if (level === 'success') return <CheckCircleIcon sx={{ fontSize: 20 }} />;
    return <InfoIcon sx={{ fontSize: 20 }} />;
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      // Call backend logout to trigger punch out
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    }
    setLogoutDialogOpen(false);
    setLoggingOut(false);
    logout();
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <GlobalMessageBanner drawerWidth={desktopDrawerWidth} />
      <BreakCountdownOverlay />
      <BreakPointerCapture />
      <IdleAcknowledgmentOverlay />
      {/* App Bar */}
      <AppBar
        ref={appBarRef}
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          ml: { md: `${desktopDrawerWidth}px` },
        }}
      >
        {/* Marquee Bar */}
        <MarqueeBar 
          isOnline={punchStatus.isOnline} 
          isIdle={punchStatus.isIdle}
          userName={user?.first_name}
        />
        {marketingEmbed ? (
          <Alert
            severity="info"
            variant="filled"
            sx={{
              borderRadius: 0,
              py: 0.5,
              px: 2,
              bgcolor: '#1565c0',
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
            }}
          >
            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
              Live demo preview — signed in as {user?.email || 'demo user'} with ABC sample data.
            </Typography>
          </Alert>
        ) : null}
        {!marketingEmbed && demoPreviewBackup?.token ? (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              borderRadius: 0,
              py: 0.75,
              px: 2,
              bgcolor: '#c62828',
              color: '#fff',
              '& .MuiAlert-icon': { color: '#fff' },
              '& .MuiAlert-message': { width: '100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={exitDemoUserPreview}
                sx={{ borderColor: 'rgba(255,255,255,0.85)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                Exit preview
              </Button>
            }
          >
            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
              Users preview mode — Administrator: you are browsing as{' '}
              <strong>{demoPreviewBackup.viewingAsLabel || 'demo account'}</strong>. Menus and permissions match that
              login. Exit restores your administrator session and focuses the opener when possible.
            </Typography>
          </Alert>
        ) : null}
        <Toolbar
          sx={{
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            columnGap: 0.5,
            py: 0.75,
            alignItems: 'center'
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, md: 0 }, display: { md: 'none' }, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              mr: 1,
              py: 0.25,
              // Keep chips sequential; scroll inside this strip so they never cover the profile
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: 'rgba(255,255,255,0.35)' }
            }}
          >
            <DigitalClock />
            <Tooltip title="UI version / build id (REACT_APP_UI_BUILD_STAMP from frontend image build)">
              <Box
                sx={{
                  width: 132,
                  minHeight: 46,
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1.25,
                  bgcolor: '#7DD3FC',
                  color: '#0C4A6E',
                  border: '2px solid #0EA5E9',
                  boxShadow: '0 0 0 1px rgba(14,165,233,0.35), 0 2px 8px rgba(14,165,233,0.3)',
                  textAlign: 'center',
                  lineHeight: 1.1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxSizing: 'border-box'
                }}
              >
                <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, letterSpacing: 0.15, lineHeight: 1.15 }}>
                  UI version
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 900,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    px: 0.25
                  }}
                >
                  {UI_VERSION_DISPLAY}
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Fetch live versions of all running ERP4C services">
              <Box
                component="button"
                type="button"
                onClick={openVersionsDialog}
                sx={{
                  width: 168,
                  minHeight: 46,
                  px: 0.75,
                  py: 0.35,
                  borderRadius: 1.25,
                  bgcolor: '#A7F3D0',
                  color: '#065F46',
                  border: '2px solid #10B981',
                  boxShadow: '0 0 0 1px rgba(16,185,129,0.35), 0 2px 8px rgba(16,185,129,0.25)',
                  textAlign: 'center',
                  lineHeight: 1.1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  font: 'inherit',
                  '&:hover': { bgcolor: '#6EE7B7' },
                }}
              >
                <Typography sx={{ fontSize: '0.45rem', fontWeight: 800, letterSpacing: 0.05, lineHeight: 1.15 }}>
                  Versions of All Running Services
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, lineHeight: 1.15 }}>
                  Click to view
                </Typography>
              </Box>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              gap: 0.5,
              flexShrink: 0,
              minWidth: 0,
              maxWidth: { xs: '100%', md: 520 },
              mr: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.75, flexWrap: 'wrap' }}>
              <LogActivityHeaderButton canLogActivity={!punchStatus.isIdle} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<LogoutOutlinedIcon />}
                onClick={handleLogoutClick}
                sx={menuOutlinedBoxButtonSx({ tone: 'logout' })}
              >
                Logout
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
                sx={menuOutlinedBoxButtonSx({ tone: 'home' })}
              >
                Home/Landing Page
              </Button>
            </Box>

            <Tooltip title={`Full URL: ${menuNavFullUrl}`} enterDelay={400}>
              <Box
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  width: '100%',
                  minWidth: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                  bgcolor: (t) => alpha(t.palette.common.white, 0.08)
                }}
              >
                <LinkIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.9 }} />
                <Typography
                  component="span"
                  variant="caption"
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.7rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                  }}
                >
                  {menuNavPath || '/'}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Copy full page URL"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(menuNavFullUrl)
                      .then(() => toast.success('Copied page URL'))
                      .catch(() => toast.error('Could not copy URL'));
                  }}
                  sx={{ flexShrink: 0, p: 0.25 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>

          {/* Notifications */}
          <IconButton color="inherit" sx={{ mr: 1 }} onClick={handleNotifOpen} aria-label="Open notifications">
            <Badge
              badgeContent={notifBadgeCount > 99 ? '99+' : notifBadgeCount}
              color="error"
              invisible={notifBadgeCount === 0}
              overlap="circular"
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  width: 380,
                  maxWidth: 'calc(100vw - 24px)',
                  maxHeight: 'min(480px, 72vh)',
                  mt: 1,
                  borderRadius: 2,
                  overflow: 'hidden'
                }
              }
            }}
          >
            <List dense disablePadding>
              <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '40px', fontWeight: 700 }}>
                Notifications
              </ListSubheader>
              {notificationItems.map((n) => (
                <ListItemButton
                  key={n.id}
                  disabled={!n.path}
                  onClick={() => {
                    if (n.path) {
                      navigate(n.path);
                      handleNotifClose();
                    }
                  }}
                  sx={{
                    alignItems: 'flex-start',
                    py: 1.25,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }
                  }}
                >
                  <Box sx={{ color: notifLevelColor(n.level), mr: 1.25, mt: 0.25 }}>{notifLevelIcon(n.level)}</Box>
                  <ListItemText
                    primary={n.title}
                    secondary={n.subtitle}
                    primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', sx: { whiteSpace: 'normal', mt: 0.5 } }}
                  />
                  {n.path ? (
                    <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0, mt: 0.5 }} fontSize="small" />
                  ) : null}
                </ListItemButton>
              ))}
            </List>
            <Box sx={{ px: 2, py: 1.5, bgcolor: (t) => alpha(t.palette.divider, 0.35) }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Pulled from punch status & break timer
                </Typography>
                <Button size="small" onClick={() => fetchPunchStatus()}>
                  Refresh
                </Button>
              </Stack>
            </Box>
          </Popover>

          {/* User Menu */}
          <Box
            onClick={handleMenuOpen}
            role="button"
            tabIndex={0}
            aria-label="Open profile menu"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleMenuOpen(e);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.85,
              flexShrink: 0,
              zIndex: 2,
              ml: 0.5,
              pl: 0.55,
              pr: 1.25,
              py: 0.4,
              borderRadius: 999,
              cursor: 'pointer',
              border: '2px solid',
              borderColor: '#67E8F9',
              background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 55%, #4F46E5 100%)',
              boxShadow: '0 2px 10px rgba(37,99,235,0.45)',
              transition: 'filter 0.15s, box-shadow 0.15s, transform 0.15s',
              '&:hover': {
                filter: 'brightness(1.08)',
                boxShadow: '0 3px 14px rgba(37,99,235,0.55)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: '#F0F9FF',
                color: '#0369A1',
                border: '2px solid',
                borderColor: 'rgba(255,255,255,0.95)'
              }}
            >
              <PersonIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Typography
              component="span"
              noWrap
              sx={{
                display: { xs: 'none', sm: 'inline' },
                fontWeight: 800,
                color: '#FFFFFF',
                fontSize: '0.84rem',
                letterSpacing: 0.2,
                lineHeight: 1.2,
                maxWidth: { sm: 168, md: 220 },
                textShadow: '0 1px 2px rgba(15,23,42,0.35)'
              }}
            >
              {user?.first_name || ''}
              {user?.first_name || user?.last_name ? ', ' : ''}
              {user?.last_name || ''}
            </Typography>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { width: 220, mt: 1 }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile}>
              <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
              <Settings sx={{ mr: 1.5, fontSize: 20 }} />
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
              <Logout sx={{ mr: 1.5, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Component versions dialog */}
      <Dialog
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Versions of All Running Services</DialogTitle>
        <DialogContent dividers>
          {versionsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!versionsLoading && versionsError && (
            <Alert severity="error">{versionsError}</Alert>
          )}
          {!versionsLoading && versionsData && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Checked {versionsData.checkedAt ? new Date(versionsData.checkedAt).toLocaleString() : '—'}
                {versionsData.releaseHint ? ` · backend release ${versionsData.releaseHint}` : ''}
              </Typography>
              <Stack spacing={1}>
                {(versionsData.components || []).map((c) => {
                  const color = c.color || { bg: '#E5E7EB', border: '#9CA3AF', text: '#111827' };
                  const unreachable = c.status === 'unreachable' || c.status === 'error';
                  return (
                    <Box
                      key={c.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: color.bg,
                        color: color.text,
                        border: `2px solid ${color.border}`,
                        opacity: unreachable ? 0.85 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          borderRadius: 1,
                          bgcolor: color.border,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {c.upstream && (
                            <Box
                              title="Upstream third-party image (not ERP4C version format)"
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: '#EF4444',
                                border: '2px solid #111827',
                                flexShrink: 0,
                                boxSizing: 'border-box',
                              }}
                            />
                          )}
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, lineHeight: 1.2 }}>
                            {c.label}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: '0.95rem',
                            fontWeight: 900,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            wordBreak: 'break-word',
                          }}
                        >
                          {c.version || 'unknown'}
                        </Typography>
                        {c.imageTag && (
                          <Typography
                            sx={{
                              mt: 0.2,
                              fontSize: '0.78rem',
                              fontWeight: 700,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                              wordBreak: 'break-word',
                              opacity: 0.9,
                            }}
                          >
                            Image: {c.imageTag}
                          </Typography>
                        )}
                        {(c.note || c.error || c.status) && (
                          <Typography sx={{ mt: 0.25, fontSize: '0.7rem', opacity: 0.85 }}>
                            {c.error || c.note || (c.status && c.status !== 'ok' ? c.status : '')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.info.main, 0.08),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.info.main, 0.25),
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 800,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                mb: 0.5,
              }}
            >
              Format: MAJOR.YYYY.WW.MINOR
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.45 }}>
              <strong>MAJOR</strong> — product generation (breaking / major line).{' '}
              <strong>YYYY</strong> — calendar year of the release. <strong>WW</strong> — ISO week number
              (1–53) when the build was cut. <strong>MINOR</strong> — incremental release within that week.
              Local images may append a short build suffix (e.g. <code>-661614</code>).
            </Typography>
            <Box
              sx={{
                mt: 1.25,
                pt: 1.25,
                borderTop: '1px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#EF4444',
                  border: '2px solid #111827',
                  flexShrink: 0,
                  mt: '2px',
                  boxSizing: 'border-box',
                }}
              />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.45 }}>
                <strong>Upstream marker (red circle):</strong>{' '}
                {versionsData?.upstreamLegend ||
                  'These components use third-party container images maintained upstream — not ERP4C. They do not follow MAJOR.YYYY.WW.MINOR. PostgreSQL uses the Docker Official postgres image (PostgreSQL Global Development Group). Jitsi Meet uses official jitsi/* images (Jitsi / 8x8 community).'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={openVersionsDialog} disabled={versionsLoading}>
              Refresh
            </Button>
            <Button onClick={() => setVersionsOpen(false)} variant="contained">
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You are about to log out of {displayCompanyName}.
          </Alert>
          <Typography variant="body1" sx={{ mb: 1 }}>
            The system will automatically <strong>Punch Out</strong> your time card when you logout.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your total working hours for today will be recorded. Make sure you have completed all your tasks before logging out.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLogoutCancel} variant="outlined" disabled={loggingOut}>
            Cancel
          </Button>
          <Button 
            onClick={handleLogoutConfirm} 
            variant="contained" 
            color="error"
            disabled={loggingOut}
          >
            {loggingOut ? 'Logging out...' : 'Logout & Punch Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sidebar */}
      <Sidebar
        drawerWidthExpanded={DRAWER_EXPANDED}
        desktopDrawerWidth={desktopDrawerWidth}
        navCompact={navCompact}
        sidebarPinned={sidebarPinned}
        onToggleSidebarPin={handleToggleSidebarPin}
        onToggleSidebarCollapsed={handleToggleSidebarCollapsed}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          // Match live AppBar height (marquee + stacked toolbar / Full URL row)
          pt: `${appBarHeight}px`,
          px: { xs: 2, md: 3 },
          pb: 3
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            ...(demoPreviewBackup?.token
              ? {
                  border: '4px solid #c62828',
                  borderRadius: 2,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }
              : {}),
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <RouteErrorBoundary
              key={`${location.pathname}-${routeErrorBoundaryKey}`}
              onReset={() => setRouteErrorBoundaryKey((k) => k + 1)}
            >
              <Outlet />
            </RouteErrorBoundary>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
